'use client';

import { useState, useLayoutEffect } from 'react';
import { QueryProvider } from '@/core/providers/QueryProvider';
import { ThemeProvider } from '@/core/providers/ThemeProvider';
import { ThemeApplier } from '@/core/providers/ThemeApplier';
import { AuthProvider } from '@/core/providers/AuthProvider';
import { TenantProvider } from '@/core/providers/TenantProvider';
import { ToastProvider } from '@/core/providers/ToastProvider';
import { LoggerProvider } from '@/core/providers/LoggerProvider';
import { OfflineProvider } from '@/core/providers/OfflineProvider';
import { UpdateBanner } from '@/shared/feedback/UpdateBanner';
import { ChangelogDrawer } from '@/shared/feedback/ChangelogDrawer';
import { CallProvider } from '@/core/providers/CallProvider';
import { MusicProvider } from '@/core/providers/MusicProvider';
import { ModalStackProvider } from '@/shared/ui/Modal';
import { LocaleContext } from '@/core/i18n/locale.context';
import { useLocaleStore } from '@/core/i18n/locale.store';
import {
  type SupportedLocale,
  isSupportedLocale,
  getLocaleDir,
} from '@/core/i18n/i18n.config';

interface ProvidersProps {
  children: React.ReactNode;
  initialLocale?: SupportedLocale;
}

export function Providers({ children, initialLocale }: ProvidersProps) {
  const storeLocale = useLocaleStore((s) => s.locale);
  const setStoreLocale = useLocaleStore((s) => s.setLocale);

  // Track hydration — before hydration, use initialLocale for SSR consistency;
  // after hydration, the Zustand store is authoritative.
  const [hydrated, setHydrated] = useState(false);

  useLayoutEffect(() => {
    // Sync store with server-provided locale on first mount
    if (initialLocale) {
      const pathSegments = window.location.pathname.split('/').filter(Boolean);
      const firstSegment = pathSegments[0];
      const hasLocalePrefix = firstSegment && isSupportedLocale(firstSegment);
      if (!hasLocalePrefix && storeLocale !== initialLocale) {
        setStoreLocale(initialLocale);
      }
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- hydration guard: mounted flag has no external-store equivalent
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSR: Zustand defaults to 'en' (no window). Use initialLocale from cookie.
  // Client hydration: same initialLocale → no mismatch with server HTML.
  // After hydration: storeLocale is live and tracks user changes.
  const effectiveLocale = hydrated ? storeLocale : (initialLocale ?? storeLocale);

  // Keep <html> lang and dir in sync with active locale
  useLayoutEffect(() => {
    if (!hydrated) return;
    const html = document.documentElement;
    if (html.lang !== storeLocale) {
      html.lang = storeLocale;
    }
    const newDir = getLocaleDir(storeLocale);
    if (html.dir !== newDir) {
      html.dir = newDir;
    }
  }, [hydrated, storeLocale]);

  return (
    <LoggerProvider>
      <QueryProvider>
        <ThemeProvider>
          <ThemeApplier />
          <AuthProvider>
            {/* The impersonation banner lives in the (pms) and (chain) layouts,
                inside the content column; rendering it here too showed it twice. */}
            <LocaleContext.Provider value={effectiveLocale}>
              <TenantProvider>
                <OfflineProvider>
                  <ModalStackProvider>
                    {/*
                      ToastProvider MUST sit above CallProvider.

                      CallProvider renders <IncomingCallHost />, which calls
                      useToast(). With the order reversed, that hook ran before
                      its context existed and threw "useToastContext must be
                      used within ToastProvider" — during SSR, so the whole
                      route returned 500 and the error boundary rendered
                      "Something went wrong" on EVERY authenticated page.

                      A provider may only consume contexts provided ABOVE it.
                    */}
                    <ToastProvider>
                      <CallProvider>
                        {/*
                          MusicProvider is innermost on purpose. It owns the one
                          <audio> element, which must never remount — every route
                          group's layout does, so anything above this that the
                          player depended on would stop the music when someone
                          crossed from /dashboard to /admin.

                          It also has to sit INSIDE ToastProvider, AuthProvider
                          and LocaleContext: the components it mounts call
                          useToast() and t(). That is the same constraint
                          CallProvider has, and getting it wrong 500s every
                          authenticated route during SSR.
                        */}
                        <MusicProvider>
                          {children}
                          <UpdateBanner />
                          <ChangelogDrawer />
                        </MusicProvider>
                      </CallProvider>
                    </ToastProvider>
                  </ModalStackProvider>
                </OfflineProvider>
              </TenantProvider>
            </LocaleContext.Provider>
          </AuthProvider>
        </ThemeProvider>
      </QueryProvider>
    </LoggerProvider>
  );
}
